import React, { useState } from 'react'
import styled from '@emotion/styled'
import Button from '@components/common/form/Button'
import {
  OverrideType,
  AddReason,
  CancelReason,
  ScheduleOverride,
} from '../types'
import {
  OVERRIDE_TYPE_LABELS,
  ADD_REASON_LABELS,
  CANCEL_REASON_LABELS,
} from '../constants'
import { formatCutoffTime, parseCutoffTimeInput } from '../utils'

interface Props {
  initialDate?: string
  initialType?: OverrideType
  initialOverride?: ScheduleOverride
  onSubmit: (data: {
    date: string
    overrideType: OverrideType
    reason: AddReason | CancelReason | null
    notes: string | null
    cutoffDaysBefore: number | null
    cutoffTime: number | null
    dropoffTime: number | null
  }) => void
  onDelete?: () => void
  onClose: () => void
}

const AddOverrideModal: React.FC<Props> = ({
  initialDate,
  initialType,
  initialOverride,
  onSubmit,
  onDelete,
  onClose,
}) => {
  const isEditing = !!initialOverride

  const [date, setDate] = useState(initialOverride?.date ?? initialDate ?? '')
  const [overrideType, setOverrideType] = useState<OverrideType>(
    initialOverride?.overrideType ?? initialType ?? OverrideType.Cancel,
  )
  const [reason, setReason] = useState<string>(initialOverride?.reason ?? '')
  const [notes, setNotes] = useState(initialOverride?.notes ?? '')
  const [hasCutoff, setHasCutoff] = useState(
    initialOverride?.cutoffDaysBefore != null,
  )
  const [cutoffDaysBefore, setCutoffDaysBefore] = useState(
    initialOverride?.cutoffDaysBefore ?? 1,
  )
  const [cutoffTime, setCutoffTime] = useState(
    initialOverride?.cutoffTime ?? 1400,
  )
  const [hasDropoff, setHasDropoff] = useState(
    initialOverride?.dropoffTime != null,
  )
  const [dropoffTime, setDropoffTime] = useState(
    initialOverride?.dropoffTime ?? 1130,
  )

  const reasonOptions =
    overrideType === OverrideType.Add
      ? Object.entries(ADD_REASON_LABELS)
      : overrideType === OverrideType.Cancel
        ? Object.entries(CANCEL_REASON_LABELS)
        : []

  const handleSubmit = () => {
    if (!date) {
      return
    }
    onSubmit({
      date,
      overrideType,
      reason: (reason || null) as AddReason | CancelReason | null,
      notes: notes || null,
      cutoffDaysBefore: hasCutoff ? cutoffDaysBefore : null,
      cutoffTime: hasCutoff ? cutoffTime : null,
      dropoffTime: hasDropoff ? dropoffTime : null,
    })
  }

  return (
    <Backdrop onClick={onClose}>
      <Modal
        onClick={(e) => e.stopPropagation()}
        data-testid="add-override-modal"
      >
        <ModalHeader>
          {isEditing ? 'Edit Override' : 'Add Override'}
        </ModalHeader>

        <Field>
          <label>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="override-date-input"
          />
        </Field>

        <Field>
          <label>Type</label>
          <select
            value={overrideType}
            onChange={(e) => {
              setOverrideType(e.target.value as OverrideType)
              setReason('')
            }}
            data-testid="override-type-select"
          >
            {Object.entries(OVERRIDE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        {reasonOptions.length > 0 && (
          <Field>
            <label>Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="override-reason-select"
            >
              <option value="">Select a reason...</option>
              {reasonOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field>
          <label>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes..."
            rows={2}
            data-testid="override-notes-input"
          />
        </Field>

        {overrideType !== OverrideType.Cancel && (
          <Field>
            <label>
              <input
                type="checkbox"
                checked={hasCutoff}
                onChange={(e) => setHasCutoff(e.target.checked)}
              />{' '}
              Custom cutoff for this date
            </label>
            {hasCutoff && (
              <CutoffRow>
                <label>
                  Days before:
                  <input
                    type="number"
                    min={0}
                    max={14}
                    value={cutoffDaysBefore}
                    onChange={(e) =>
                      setCutoffDaysBefore(parseInt(e.target.value, 10) || 0)
                    }
                  />
                </label>
                <label>
                  Time:
                  <input
                    type="time"
                    value={formatCutoffTime(cutoffTime)}
                    onChange={(e) =>
                      setCutoffTime(parseCutoffTimeInput(e.target.value))
                    }
                  />
                </label>
              </CutoffRow>
            )}
          </Field>
        )}

        {overrideType !== OverrideType.Cancel && (
          <Field>
            <label>
              <input
                type="checkbox"
                checked={hasDropoff}
                onChange={(e) => setHasDropoff(e.target.checked)}
              />{' '}
              Custom dropoff time for this date
            </label>
            {hasDropoff && (
              <CutoffRow>
                <label>
                  Dropoff Time:
                  <input
                    type="time"
                    value={formatCutoffTime(dropoffTime)}
                    onChange={(e) =>
                      setDropoffTime(parseCutoffTimeInput(e.target.value))
                    }
                  />
                </label>
              </CutoffRow>
            )}
          </Field>
        )}

        <ButtonRow>
          <Button
            size="sm"
            label={isEditing ? 'Save Changes' : 'Add Override'}
            onClick={handleSubmit}
            disabled={!date}
          />
          {isEditing && onDelete && (
            <Button
              size="sm"
              label="Delete Override"
              onClick={onDelete}
              backgroundColor="#E57373"
            />
          )}
          <Button
            size="sm"
            label="Cancel"
            onClick={onClose}
            backgroundColor="#9E9E9E"
          />
        </ButtonRow>
      </Modal>
    </Backdrop>
  )
}

export default AddOverrideModal

// ── Styled Components ──

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const Modal = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  min-width: 420px;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`

const ModalHeader = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: #212121;
`

const Field = styled.div`
  margin-bottom: 14px;

  > label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #424242;
    margin-bottom: 4px;
  }

  input[type='date'],
  input[type='text'],
  select,
  textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #bdbdbd;
    border-radius: 4px;
    font-size: 13px;
    box-sizing: border-box;
  }

  textarea {
    resize: vertical;
  }
`

const CutoffRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;

  label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
  }

  input[type='number'] {
    width: 60px;
    padding: 4px;
    border: 1px solid #bdbdbd;
    border-radius: 3px;
  }

  input[type='time'] {
    padding: 4px;
    border: 1px solid #bdbdbd;
    border-radius: 3px;
  }
`

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`
